import { type ItemTypeValue, QuestRewardType } from '@dreamgame/shared';
import { useState } from 'preact/hooks';
import { tv } from 'tailwind-variants';
import { itemName, skillName, t } from '../../state/i18n';
import {
  getChainProgress,
  getQuestChainDisplay,
  type QuestChainItem,
  type QuestDisplayStatus,
  questProgress,
} from '../../state/quests';

// ============================================================================
// Icons
// ============================================================================

const CompletedIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    class="w-5 h-5 flex-shrink-0"
    aria-hidden="true"
  >
    <circle cx="10" cy="10" r="9" fill="#16a34a" />
    <path
      d="M6.5 10l2.5 2.5L13.5 7"
      stroke="white"
      stroke-width="2"
      fill="none"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

const ActiveIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    class="w-5 h-5 flex-shrink-0"
    aria-hidden="true"
  >
    <circle cx="10" cy="10" r="7.5" fill="none" stroke="#d4a855" stroke-width="2" />
    <circle cx="10" cy="10" r="3.5" fill="#d4a855" />
  </svg>
);

const LockedIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    class="w-5 h-5 flex-shrink-0"
    aria-hidden="true"
  >
    <circle cx="10" cy="10" r="7.5" fill="none" stroke="#5c4a3d" stroke-width="1.5" opacity="0.4" />
    <rect x="7.5" y="10" width="5" height="3.5" rx="0.5" fill="#5c4a3d" opacity="0.4" />
    <path
      d="M8.5 10V8.5a1.5 1.5 0 013 0V10"
      fill="none"
      stroke="#5c4a3d"
      stroke-width="1.2"
      opacity="0.4"
    />
  </svg>
);

const ScrollIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    class="w-4 h-4 text-gold-filigree flex-shrink-0"
    aria-hidden="true"
  >
    <path d="M4.5 2A1.5 1.5 0 003 3.5v13A1.5 1.5 0 004.5 18h11a1.5 1.5 0 001.5-1.5v-13A1.5 1.5 0 0015.5 2h-11zM6 6h8v1.5H6V6zm0 3.5h8V11H6V9.5zm0 3.5h5v1.5H6V13z" />
  </svg>
);

const StarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    class="w-4 h-4 text-gold-bright flex-shrink-0 mt-0.5"
    aria-hidden="true"
  >
    <path d="M10 1l2.39 4.84 5.34.78-3.87 3.77.91 5.32L10 13.27l-4.77 2.51.91-5.32L2.27 6.69l5.34-.78L10 1z" />
  </svg>
);

const chevronStyle = tv({
  base: 'w-3.5 h-3.5 text-rs-stone-light transition-transform duration-200',
  variants: {
    expanded: {
      true: 'rotate-180',
      false: '',
    },
  },
});

const ChevronIcon = ({ expanded }: { expanded: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    class={chevronStyle({ expanded })}
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
      clipRule="evenodd"
    />
  </svg>
);

// ============================================================================
// Styles
// ============================================================================

const containerStyle = tv({
  base: 'fixed top-20 left-4 flex flex-col rounded-lg bg-rs-stone-dark/90 backdrop-blur-sm border border-gold-filigree/25 z-40 transition-all duration-200 overflow-hidden shadow-rs-panel',
  variants: {
    expanded: {
      true: 'w-72',
      false: 'w-56',
    },
  },
});

const nameStyle = tv({
  base: 'text-sm font-medium leading-tight',
  variants: {
    status: {
      completed: 'text-green-400/80',
      active: 'text-parchment-light',
      locked: 'text-rs-stone-light/50',
    },
  },
});

const rewardStyle = tv({
  base: 'text-xs leading-tight mt-0.5 block',
  variants: {
    status: {
      completed: 'text-green-400/40',
      active: 'text-gold-filigree/70',
      locked: 'text-rs-stone-light/30',
    },
  },
});

const connectorStyle = tv({
  base: 'w-0.5 flex-1 ml-[9px] min-h-2',
  variants: {
    status: {
      completed: 'bg-green-600/40',
      active: 'bg-gold-filigree/20',
      locked: 'bg-rs-stone-light/15',
    },
  },
});

// ============================================================================
// Helpers
// ============================================================================

const StatusIcon = ({ status }: { status: QuestDisplayStatus }) => {
  if (status === 'completed') {
    return <CompletedIcon />;
  }
  if (status === 'active') {
    return <ActiveIcon />;
  }
  return <LockedIcon />;
};

const formatRewards = ({ rewards }: { rewards: QuestChainItem['rewards'] }): string => {
  const parts: string[] = [];
  for (const reward of rewards) {
    if (reward.type === QuestRewardType.ITEMS) {
      for (const item of reward.items) {
        parts.push(`${item.quantity}x ${itemName({ itemType: item.itemType as ItemTypeValue })}`);
      }
    } else if (reward.type === QuestRewardType.SKILL_XP) {
      parts.push(`${reward.xp} ${skillName({ skillId: reward.skillId })} XP`);
    } else if (reward.type === QuestRewardType.SPAWN_PAWN) {
      parts.push(t('ui.labels.newColonist'));
    }
  }
  return parts.join(', ');
};

// ============================================================================
// Sub-components
// ============================================================================

const ChainRewardBanner = () => (
  <div class="flex items-start gap-2 px-3 py-2 bg-gold-filigree/10 border-b border-gold-filigree/15">
    <StarIcon />
    <p class="text-xs text-gold-filigree/80 leading-snug">{t('ui.quests.chainReward')}</p>
  </div>
);

const QuestStep = ({ quest, isLast }: { quest: QuestChainItem; isLast: boolean }) => {
  const rewardText = formatRewards({ rewards: quest.rewards });
  const progressPercent =
    quest.targetAmount > 0 ? Math.min((quest.currentAmount / quest.targetAmount) * 100, 100) : 0;

  return (
    <div class="flex gap-1.5">
      <div class="flex flex-col items-center flex-shrink-0 w-5">
        <StatusIcon status={quest.status} />
        {!isLast && <div class={`my-1 ${connectorStyle({ status: quest.status })}`} />}
      </div>

      <div class="flex-1 pb-3 min-w-0 -mt-0.5">
        <span class={nameStyle({ status: quest.status })}>{quest.name}</span>

        {quest.status === 'active' && (
          <>
            <p class="text-xs text-rs-parchment-dark/70 leading-tight mt-0.5">
              {quest.description}
            </p>
            <div class="flex items-center gap-2 mt-1">
              <div class="flex-1 h-1 rounded-full bg-rs-stone/60 overflow-hidden">
                <div
                  class="h-full rounded-full bg-gold-filigree transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span class="text-xs text-rs-parchment-dark/60 flex-shrink-0">
                {quest.currentAmount}/{quest.targetAmount}
              </span>
            </div>
          </>
        )}

        {rewardText && quest.status !== 'completed' && (
          <span class={rewardStyle({ status: quest.status })}>
            {t('ui.quests.stepReward', { reward: rewardText })}
          </span>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const QuestsPanel = () => {
  const [expanded, setExpanded] = useState(true);

  // Subscribe to quest progress signal
  const _ = questProgress.value;
  const quests = getQuestChainDisplay();
  const { completed, total } = getChainProgress();
  const progressPercent = total > 0 ? (completed / total) * 100 : 0;

  return (
    <div class={containerStyle({ expanded })}>
      <button
        type="button"
        class="flex items-center gap-2 px-3 py-2 border-b border-gold-filigree/15 cursor-pointer select-none w-full"
        onClick={() => setExpanded(!expanded)}
      >
        <ScrollIcon />
        <span class="font-medieval text-sm text-gold-filigree tracking-wide flex-1 text-left">
          {t('ui.quests.chainTitle')}
        </span>
        <span class="text-xs text-rs-parchment-dark/60">
          {completed}/{total}
        </span>
        <ChevronIcon expanded={expanded} />
      </button>

      {expanded && (
        <>
          <ChainRewardBanner />
          <div class="overflow-y-auto max-h-[28rem] px-3 pt-2 pb-2">
            {quests.map((quest, i) => (
              <QuestStep key={quest.type} quest={quest} isLast={i === quests.length - 1} />
            ))}
            <div class="h-4" />
            <div class="border-t border-gold-filigree/30 pt-2">
              <div class="flex items-center gap-2">
                <div class="flex-1 h-1.5 rounded-full bg-rs-stone/50 overflow-hidden">
                  <div
                    class="h-full rounded-full bg-green-600/70 transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <span class="text-xs text-rs-parchment-dark/60 flex-shrink-0">
                  {completed}/{total} {t('ui.quests.complete')}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
